// components/Cards/OrganizerCard.tsx
import React from 'react';
import { Card, Title, Text, Stack, Group, ActionIcon, Image, Skeleton } from '@mantine/core';
import { Link } from '@inertiajs/react';
import { IconWorld, IconBrandFacebook, IconBrandInstagram, IconArrowRight } from '@tabler/icons-react';

interface Organizer {
  id: number;
  name: string;
  slug: string;
  description?: string;
  website_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  organization_image_url?: string;
}

interface OrganizerCardProps {
  organizer: Organizer;
  onCardClick?: (organizer: Organizer) => void;
}

const OrganizerCard: React.FC<OrganizerCardProps> = ({ organizer, onCardClick }) => {
  return (
    <Card
      key={organizer.id}
      shadow="sm"
      padding={0}
      radius="md"
      withBorder
      h="100%"
      style={{
        backgroundColor: '#3d3c3e',
        borderColor: '#5c5c5c',
        overflow: 'hidden',
      }}
      onClick={onCardClick ? () => onCardClick(organizer) : undefined}
      sx={onCardClick ? { cursor: 'pointer' } : undefined}
    >
      <div style={{ width: '100%', paddingTop: '56.25%', position: 'relative' }}>
        <Image
          src={organizer.organization_image_url || 'https://placehold.co/600x400?text=No+Image'}
          alt={organizer.name}
          placeholder={<Skeleton height="100%" radius={0} animate />}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          radius={0}
        />
      </div>
      <div style={{ padding: '12px' }}>
        <Stack h="100%" justify="space-between">
          <div>
            <Title order={4} mb="0" className="rubik" style={{ color: '#f2f2f2' }}>
              <Link
                href={`/o/${organizer.slug}`}
                style={{
                  color: '#f2f2f2',
                  textDecoration: 'none',
                }}
                onClick={(e) => onCardClick && e.preventDefault()}
              >
                {organizer.name}
              </Link>
            </Title>
            <Text size="sm" className="poppins" lineClamp={2} mb="0" style={{ color: '#d1d1d1', fontSize: 12 }}>
              {organizer.description}
            </Text>
          </div>
          <div>
            <Group gap="0">
              {organizer.website_url && (
                <ActionIcon
                  component="a"
                  href={organizer.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="subtle"
                  color="#EA580C"
                  size="lg"
                  aria-label="Website"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconWorld size={20} />
                </ActionIcon>
              )}
              {organizer.facebook_url && (
                <ActionIcon
                  component="a"
                  href={organizer.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="subtle"
                  color="#EA580C"
                  size="lg"
                  aria-label="Facebook"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconBrandFacebook size={20} />
                </ActionIcon>
              )}
              {organizer.instagram_url && (
                <ActionIcon
                  component="a"
                  href={organizer.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="subtle"
                  color="#EA580C"
                  size="lg"
                  aria-label="Instagram"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconBrandInstagram size={20} />
                </ActionIcon>
              )}
              <ActionIcon
                component={Link}
                href={`/o/${organizer.slug}`}
                variant="filled"
                color="#EA580C"
                size="lg"
                ml="auto"
                aria-label="View Organizer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onCardClick) {
                    e.preventDefault();
                    onCardClick(organizer);
                  }
                }}
              >
                <IconArrowRight size={20} />
              </ActionIcon>
            </Group>
          </div>
        </Stack>
      </div>
    </Card>
  );
};

export default OrganizerCard;
